import React from 'react';
import { PropTypes } from 'prop-types';
import { Field, useField } from 'formik';
import classNames from 'classnames';
import styled from 'styled-components';
import Asterisk from '@atoms/Asterisk';
import colors from '@general/colors';
import { BodySmall } from '@react/styledComponents';

const ContainerLabelStyled = styled.label`
    display: ${props => (props.inline ? 'inline-flex' : 'flex')};
    cursor: pointer;
    margin-bottom: 0;
    position: relative;

    &.disabled {
        cursor: not-allowed;
        opacity: 1;
    }

    &:hover .checkbox {
        border-color: ${colors.darkGray};
    }

    &:focus-within .checkbox {
        border-color: ${colors.darkGray};
    }
`;

const InputStyled = styled.input`
    position: absolute;
    opacity: 0;

    &:checked ~ span {
        &::after {
            display: block;
        }
    }
`;

const CheckBoxStyled = styled.span`
    background-color: white;
    border: 2px solid ${colors.lightGray};
    height: 20px;
    left: 0;
    opacity: 1 !important;
    position: absolute;
    top: 0;
    transition: border-color 0.2s ease-in-out;
    width: 20px;

    &::after {
        border: solid black;
        border-width: 0 1px 1px 0;
        content: '';
        display: none;
        height: 8px;
        left: 6px;
        position: absolute;
        top: 3px;
        transform: rotate(45deg);
        width: 4px;
    }

    &.disabled {
        border: 2px solid ${colors.lightGray} !important;

        &::after {
            border: solid ${colors.mediumGray} !important;
            border-width: 0 1px 1px 0 !important;
        }
    }
`;

const TextInputStyled = styled.input`
    border: 1px solid ${colors.lightGray};

    &[disabled] {
        background-color: ${colors.ultraLightGray};
        color: ${colors.darkGray};
    }
`;

// https://formik.org/docs/examples/checkboxes
// This supports single checkboxes - no value, different names
// and group checkboxes with the same name, different values
const CheckboxStyled = ({ name, label, value, onChange = () => { }, disabled, required = false }) => {
    const id = value ? `checkbox-${value}` : `checkbox-${name}`;
    const [field, meta, helpers] = useField(name);
    const hasError = meta.touched && meta.error;
    const labelClasses = classNames('form-check-label', {
        'form-check-label--disabled': disabled,
    });
    const groupClasses = classNames('form-group', { 'has-danger': hasError });

    return (
        <div className={groupClasses}>
            <div className="form-check">
                <label className={labelClasses} htmlFor={id}>
                    <Field
                        id={id}
                        className="form-check-input"
                        name={name}
                        type="checkbox"
                        as={InputStyled}
                        value={value}
                        onChange={() => {
                            console.log("field value changed", field.value);
                            helpers.setValue(!field.value);
                            onChange(!field.value);
                        }}
                        disabled={disabled}
                    />{' '}
                    <CheckBoxStyled className={classNames('checkbox', { disabled })} />
                    <BodySmall className='ml-1'>{label}{required && <Asterisk />}</BodySmall>
                </label>
            </div>
            {hasError && <BodySmall className="mt-0.25rem text-error">{meta.error}</BodySmall>}
        </div>
    );
};

CheckboxStyled.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
};

export default CheckboxStyled;
